import React from 'react';
import { Button, Typography } from 'antd';
import { ArrowRight, Sprout, CloudRain, Bug, TrendingUp, Wifi, Battery, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import HeroImg4 from '@/assets/images/RAW images/Qodearis_img4.png';
import HeroImg2 from '@/assets/images/RAW images/Qodearis_img2.png';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const { Title, Text, Paragraph } = Typography;

export const Home: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Helmet>
        <title>QodeAris - Empowering Farmers with Smart Agriculture</title>
        <meta name="description" content="QodeAris provides affordable, solar-powered smart farming solutions like DhartiNetra. Monitor your farm 24x7 with AI-driven insights." />
        <meta name="keywords" content="smart farming, agriculture IoT, DhartiNetra, farm monitoring, agritech india" />
      </Helmet>
      <Navbar />

      <main id="main-content">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="mb-4">
                <Text className="text-green-600 font-semibold text-lg">Welcome to QodeAris</Text>
              </div>
              <Title level={1} className="text-5xl md:text-6xl font-bold mb-6">
                Empowering Farmers with
                <br />
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                  Smart Agriculture
                </span>
              </Title>
              <Paragraph className="text-xl text-gray-600 dark:text-gray-300 mb-8">
                QodeAris is an agritech innovation company building affordable, scalable, and impact-driven 
                solutions for Indian agriculture.
              </Paragraph>
              <div className="flex gap-4 flex-wrap">
                <a href="/products/dhartinetra">
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRight className="w-5 h-5" aria-hidden="true" />}
                    className="h-12 px-8 text-lg bg-green-600 hover:bg-green-700"
                    aria-label="Explore our products"
                  >
                    Explore DhartiNetra
                  </Button>
                </a>
                <a href="/contact">
                  <Button
                    size="large"
                    className="h-12 px-8 text-lg"
                    aria-label="Contact us"
                  >
                    Get in Touch
                  </Button>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img 
                src={HeroImg4} 
                alt="DhartiNetra Smart Farming Device" 
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-green-600 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">24×7</div>
                <div className="text-lg text-green-100">Farm Monitoring</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">100%</div>
                <div className="text-lg text-green-100">Solar Powered</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">AI</div>
                <div className="text-lg text-green-100">Powered Insights</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <div className="text-4xl md:text-5xl font-bold mb-2">Multi</div>
                <div className="text-lg text-green-100">Language Support</div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Featured Product Section */}
        <section className="bg-white dark:bg-gray-800 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Text className="text-green-600 font-semibold text-lg mb-2 block">Our Flagship Product</Text>
              <Title level={2} className="mb-4">DhartiNetra – Farm Rakshak</Title>
              <Paragraph className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Your Farm's Digital Guardian. AI-powered IoT platform that monitors your farm 24×7, 
                providing actionable advice in your local language.
              </Paragraph>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <img 
                  src={HeroImg2} 
                  alt="DhartiNetra Smart Farming Device" 
                  className="rounded-xl w-full shadow-lg"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Title level={3} className="mb-4">Smart Farming Made Simple</Title>
                <Paragraph className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                  DhartiNetra is a solar-powered smart farm monitoring system combined with an AI-driven 
                  mobile and web platform. It works as your 24×7 Farm Rakshak, collecting real-time field data, 
                  analyzing it intelligently, and guiding farmers with clear next steps.
                </Paragraph>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 text-xl">✓</span>
                    <Text className="text-gray-700 dark:text-gray-300">Solar-powered with 24×7 monitoring</Text>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 text-xl">✓</span>
                    <Text className="text-gray-700 dark:text-gray-300">AI-driven crop-specific recommendations</Text>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 text-xl">✓</span>
                    <Text className="text-gray-700 dark:text-gray-300">Multi-language mobile app support</Text>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 text-xl">✓</span>
                    <Text className="text-gray-700 dark:text-gray-300">Affordable for smallholder farmers</Text>
                  </li>
                </ul>
                <Button
                  type="primary"
                  size="large"
                  href="/product"
                  className="bg-green-600 hover:bg-green-700"
                >
                  Learn More About DhartiNetra
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Problems We Solve */}
        <section className="bg-gray-50 dark:bg-gray-800 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">The Problem We Solve</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Challenges Faced by Low-Cost Farmers
              </Text>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-lg"
              >
                <CloudRain className="w-12 h-12 text-blue-600 mb-4" aria-hidden="true" />
                <Title level={4} className="mb-2">Unpredictable Weather</Title>
                <Text className="text-gray-600 dark:text-gray-300">
                  Climate uncertainty leads to crop loss and income instability.
                </Text>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-lg"
              >
                <Bug className="w-12 h-12 text-red-600 mb-4" aria-hidden="true" />
                <Title level={4} className="mb-2">Pest & Disease Threats</Title>
                <Text className="text-gray-600 dark:text-gray-300">
                  Lack of early warning systems results in delayed action and damage.
                </Text>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-lg"
              >
                <TrendingUp className="w-12 h-12 text-orange-600 mb-4" aria-hidden="true" />
                <Title level={4} className="mb-2">No Real Data</Title>
                <Text className="text-gray-600 dark:text-gray-300">
                  Farming decisions are often based on experience or guesswork.
                </Text>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-lg"
              >
                <Wifi className="w-12 h-12 text-purple-600 mb-4" aria-hidden="true" />
                <Title level={4} className="mb-2">Limited Access to Technology</Title>
                <Text className="text-gray-600 dark:text-gray-300">
                  High-cost solutions are not designed for smallholder farmers.
                </Text>
              </motion.div>
            </div>
            <div className="text-center mt-8">
              <Text className="text-lg font-semibold text-green-700 dark:text-green-400">
                DhartiNetra is built to solve these problems—affordably, reliably, and at scale.
              </Text>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">How DhartiNetra Works</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Your 24×7 Farm Rakshak, collecting real-time field data, analyzing it intelligently, 
                and guiding farmers with clear next steps.
              </Text>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { step: '1', title: 'Easy Deployment', desc: 'Low-cost sensors installed with minimal technical effort.' },
                { step: '2', title: 'Continuous Data Collection', desc: 'Track soil moisture, temperature, humidity, nutrients, light, and wind.' },
                { step: '3', title: 'AI Analysis & Insights', desc: 'Our AI engine processes farm data with crop-specific intelligence.' },
                { step: '4', title: 'Actionable Alerts', desc: 'Receive alerts and recommendations via mobile app in local languages.' },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <Title level={4} className="mb-2">{item.title}</Title>
                  <Text className="text-gray-600 dark:text-gray-300">{item.desc}</Text>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="bg-green-50 dark:bg-gray-800 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Key Features</Title>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white dark:bg-gray-700 p-8 rounded-xl shadow-lg">
                <Sprout className="w-12 h-12 text-green-600 mb-4" aria-hidden="true" />
                <Title level={4} className="mb-4">Smart Sensors</Title>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li>✓ Soil Moisture</li>
                  <li>✓ Temperature & Humidity</li>
                  <li>✓ NPK (Soil Nutrients)</li>
                  <li>✓ Wind Speed & Direction</li>
                  <li>✓ Light Intensity (Lux)</li>
                </ul>
              </div>

              <div className="bg-white dark:bg-gray-700 p-8 rounded-xl shadow-lg">
                <Battery className="w-12 h-12 text-blue-600 mb-4" aria-hidden="true" />
                <Title level={4} className="mb-4">Solar-Powered Device</Title>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li>✓ Solar Panel</li>
                  <li>✓ DC Battery Backup</li>
                  <li>✓ Environmental Sensors</li>
                  <li>✓ Wind Antenna</li>
                  <li>✓ Rugged Mounting Pole</li>
                </ul>
              </div>

              <div className="bg-white dark:bg-gray-700 p-8 rounded-xl shadow-lg">
                <Camera className="w-12 h-12 text-purple-600 mb-4" aria-hidden="true" />
                <Title level={4} className="mb-4">AI-Powered Analytics</Title>
                <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                  <li>✓ Crop-specific recommendations</li>
                  <li>✓ Early pest & disease detection</li>
                  <li>✓ Irrigation guidance</li>
                  <li>✓ Weather-based planning</li>
                  <li>✓ Multi-language support</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose QodeAris */}
        <section className="py-16 bg-white dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Title level={2} className="mb-4">Why Choose QodeAris?</Title>
              <Text className="text-lg text-gray-600 dark:text-gray-300">
                Built for Indian farmers, by people who understand agriculture
              </Text>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">💰</span>
                </div>
                <Title level={4} className="mb-3">Affordable Pricing</Title>
                <Text className="text-gray-600 dark:text-gray-300">
                  Designed specifically for small and low-cost farmers with flexible payment options
                </Text>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🌾</span>
                </div>
                <Title level={4} className="mb-3">Local Language Support</Title>
                <Text className="text-gray-600 dark:text-gray-300">
                  Get insights and recommendations in Hindi, Marathi, and other regional languages
                </Text>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🤝</span>
                </div>
                <Title level={4} className="mb-3">Dedicated Support</Title>
                <Text className="text-gray-600 dark:text-gray-300">
                  Training, installation assistance, and ongoing support from our expert team
                </Text>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Title level={2} className="text-white mb-4">
              Ready to Transform Your Farm?
            </Title>
            <Paragraph className="text-xl mb-8 text-white/90">
              Join the smart farming revolution. DhartiNetra helps you increase yields, 
              reduce costs, and farm with confidence.
            </Paragraph>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="/contact">
                <button className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors h-12 text-lg">
                  Request a Demo
                </button>
              </a>
              <a href="/about">
                <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors h-12 text-lg">
                  Learn More About Us
                </button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};
